<template>
  <div>
    <SetupPage
        page-label="Mock Exam Subjects"
        eyebrow="ASSESSMENT"
        title="Mock exam subjects"
        description="Select the subjects included in each mock exam so they can be scored, ranked and reported."
        toolbar-title="Included subjects"
        :meta="scopeLabel"
        :loader="onloader"
    >
        <template slot="side">
            <div v-if="selectedOptionId && mocksubjectitems !== null" class="ws-stat">
                <strong>{{ subjects.length }}</strong>
                <span>{{ subjects.length === 1 ? "subject" : "subjects" }}</span>
            </div>
            <button
                type="button"
                class="btn ws-primary-button"
                :disabled="!mocks.length || isSaving"
                :title="mocks.length ? 'Add a subject to a mock exam' : 'Create a mock exam first'"
                @click="openAssign"
            >
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Add subject</span>
            </button>
        </template>

        <template slot="toolbar">
            <label class="ws-scope">
                <span>Mock</span>
                <select
                    v-model="selectedMockId"
                    :disabled="mocksLoading || !mocks.length"
                    aria-label="Mock exam to show subjects for"
                    @change="onMockChange"
                >
                    <option v-if="mocksLoading" :value="0" disabled>Loading mock exams…</option>
                    <option v-else :value="0">Select mock exam</option>
                    <option v-for="item in mocks" :key="item.Id" :value="item.Id">
                        {{ mockLabel(item) }}
                    </option>
                </select>
            </label>

            <label class="ws-scope">
                <span>Option</span>
                <select
                    v-model="selectedOptionId"
                    :disabled="!selectedMockId || optionsLoading || !options.length"
                    aria-label="Option to show mock exam subjects for"
                    @change="onOptionChange"
                >
                    <option v-if="!selectedMockId" :value="0" disabled>Select mock first</option>
                    <option v-else-if="optionsLoading" :value="0" disabled>Loading options…</option>
                    <option v-else-if="!options.length" :value="0" disabled>No options</option>
                    <option v-else :value="0">Select option</option>
                    <option v-for="item in options" :key="item.Id" :value="item.Id">
                        {{ item.ClassOption }}
                    </option>
                </select>
            </label>

            <div class="ws-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input
                    v-model="searchval"
                    type="search"
                    placeholder="Search subjects"
                    aria-label="Search subjects included in the mock exam"
                    :disabled="!subjects.length"
                />
                <button
                    v-if="searchval"
                    type="button"
                    class="ws-search__clear"
                    aria-label="Clear search"
                    @click="searchval = ''"
                >
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <div v-if="mocksLoading" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading mock exams</strong>
            <span>Fetching the mock exams registered at your school.</span>
        </div>

        <div v-else-if="mocksFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Mock exams could not be loaded</strong>
            <span>Check your connection and try again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryMocks">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!mocks.length" class="ws-state">
            <i class="fa fa-file-alt" aria-hidden="true"></i>
            <strong>No mock exams available</strong>
            <span>Create a mock exam before choosing the subjects it covers.</span>
        </div>

        <div v-else-if="!selectedMockId" class="ws-state">
            <i class="fa fa-filter" aria-hidden="true"></i>
            <strong>Select a mock exam</strong>
            <span>Choose a mock exam above to see the subjects it includes.</span>
        </div>

        <div v-else-if="optionsLoading && optionitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading options</strong>
            <span>Fetching the options registered for {{ selectedMockName }}.</span>
        </div>

        <div v-else-if="optionsFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Options could not be loaded</strong>
            <span>Try loading the options for {{ selectedMockName }} again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryOptions">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!options.length" class="ws-state">
            <i class="fa fa-sitemap" aria-hidden="true"></i>
            <strong>{{ selectedMockName }} has no options</strong>
            <span>Subjects are added per option. Create an option for this class first.</span>
            <router-link
                to="/school/academic-management/options"
                class="btn ws-ghost-button"
            >
                <i class="fa fa-arrow-right" aria-hidden="true"></i>
                <span>Go to options</span>
            </router-link>
        </div>

        <div v-else-if="!selectedOptionId" class="ws-state">
            <i class="fa fa-filter" aria-hidden="true"></i>
            <strong>Select an option</strong>
            <span>Choose an option above to see the subjects included under it.</span>
        </div>

        <div v-else-if="subjectsLoading && mocksubjectitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading subjects</strong>
            <span>Fetching subjects for {{ selectedMockName }} · {{ selectedOptionName }}.</span>
        </div>

        <div v-else-if="subjectsFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Subjects could not be loaded</strong>
            <span>Try loading the subjects for {{ selectedOptionName }} again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retrySubjects">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!subjects.length" class="ws-state">
            <i class="fa fa-file-alt" aria-hidden="true"></i>
            <strong>No subjects added for {{ selectedMockName }} · {{ selectedOptionName }}</strong>
            <span>Add a subject so it can be scored in this mock exam.</span>
            <button type="button" class="btn ws-ghost-button" @click="openAssign">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Add subject</span>
            </button>
        </div>

        <div v-else-if="!filteredSubjects.length" class="ws-state">
            <i class="fa fa-search" aria-hidden="true"></i>
            <strong>No subjects match “{{ searchval.trim() }}”</strong>
            <span>Try a different subject name.</span>
            <button type="button" class="btn ws-ghost-button" @click="searchval = ''">
                Clear search
            </button>
        </div>

        <div v-else class="ws-grid" :class="{ 'is-loading': subjectsLoading }">
            <article
                v-for="(item, index) in filteredSubjects"
                :key="rowKey(item, index)"
                class="ws-card"
            >
                <span class="ws-card__index">{{ String(index + 1).padStart(2, "0") }}</span>
                <div class="ws-card__body">
                    <span class="ws-card__title">{{ subjectName(item) }}</span>
                    <span class="ws-card__meta">
                        {{ selectedMockName }} · {{ selectedOptionName }}
                    </span>
                </div>
                <div class="ws-card__actions">
                    <button
                        type="button"
                        class="ws-danger-button"
                        :disabled="isSaving"
                        :aria-label="`Remove ${subjectName(item)}`"
                        @click="removeSubject(item)"
                    >
                        <i class="fa fa-trash" aria-hidden="true"></i>
                        <span>Remove</span>
                    </button>
                </div>
            </article>
        </div>
    </SetupPage>

    <SetupDrawer
        :show.sync="formVisible"
        icon="fa-file-alt"
        eyebrow="NEW SUBJECT"
        title="Add a subject to the mock"
        :subtitle="drawerSubtitle"
        :saving="isSaving"
        submit-label="Add subject"
        :submit-disabled="submitDisabled"
        form-id="mock-subject-form"
        @close="closeForm"
        @submit="assignSubject"
    >
        <fieldset class="ws-fieldset">
            <legend>Subject details</legend>

            <div class="row">
                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="mock-class">
                            Mock exam <span class="ws-required">*</span>
                        </label>
                        <select
                            id="mock-class"
                            v-model="form.mockId"
                            class="form-control"
                            :class="{ 'is-invalid': errors.mockId }"
                            :disabled="isSaving || !mocks.length"
                            @change="onFormMockChange"
                        >
                            <option :value="0">Select mock exam</option>
                            <option v-for="item in mocks" :key="item.Id" :value="item.Id">
                                {{ mockLabel(item) }}
                            </option>
                        </select>
                        <span v-if="errors.mockId" class="ws-error" role="alert">
                            {{ errors.mockId }}
                        </span>
                    </div>
                </div>

                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="mock-option">
                            Option <span class="ws-required">*</span>
                        </label>
                        <select
                            id="mock-option"
                            v-model="form.optionId"
                            class="form-control"
                            :class="{ 'is-invalid': errors.optionId }"
                            :disabled="isSaving || !form.mockId || formOptionsLoading"
                            @change="onFormOptionChange"
                        >
                            <option v-if="!form.mockId" :value="0" disabled>
                                Select a mock exam first
                            </option>
                            <option v-else-if="formOptionsLoading" :value="0" disabled>
                                Loading options…
                            </option>
                            <option v-else-if="!formOptions.length" :value="0" disabled>
                                No options for this mock exam
                            </option>
                            <option v-else :value="0">Select option</option>
                            <option v-for="item in formOptions" :key="item.Id" :value="item.Id">
                                {{ item.ClassOption }}
                            </option>
                        </select>
                        <span v-if="errors.optionId" class="ws-error" role="alert">
                            {{ errors.optionId }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="form-group">
                <label for="mock-subject">
                    Subject <span class="ws-required">*</span>
                </label>
                <select
                    id="mock-subject"
                    v-model="form.subjectId"
                    class="form-control"
                    :class="{ 'is-invalid': errors.subjectId }"
                    :disabled="isSaving || !form.optionId || availableBusyForForm"
                    @change="clearError('subjectId')"
                >
                    <option v-if="!form.mockId" :value="0" disabled>Select a mock exam first</option>
                    <option v-else-if="formOptionsLoading" :value="0" disabled>
                        Loading options…
                    </option>
                    <option v-else-if="!formOptions.length" :value="0" disabled>
                        No options for this mock exam
                    </option>
                    <option v-else-if="!form.optionId" :value="0" disabled>
                        Select an option first
                    </option>
                    <option v-else-if="availableBusyForForm" :value="0" disabled>
                        Loading subjects…
                    </option>
                    <option v-else-if="!availableForForm.length" :value="0" disabled>
                        No subjects available
                    </option>
                    <option v-else :value="0">Select subject</option>
                    <option v-for="item in availableForForm" :key="item.Id" :value="item.Id">
                        {{ item.Title }}
                    </option>
                </select>
                <span v-if="errors.subjectId" class="ws-error" role="alert">
                    {{ errors.subjectId }}
                </span>
                <span v-else-if="availableFailedForForm" class="ws-error" role="alert">
                    Available subjects could not be loaded. Choose the option again to retry.
                </span>
                <span v-else class="ws-hint">
                    Only subjects not yet added to this mock exam and option are listed.
                </span>
            </div>

            <div v-if="showFormOptionsNotice" class="ws-notice">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>
                    This mock exam has no options yet. Create an option before adding
                    subjects.
                    <router-link to="/school/academic-management/options">
                        Go to options
                    </router-link>
                </span>
            </div>

            <div v-else-if="showFormSubjectsNotice" class="ws-notice">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span>
                    Every subject is already added for {{ formMockName }} ·
                    {{ formOptionName }}.
                </span>
            </div>
        </fieldset>
    </SetupDrawer>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({ mockId: 0, optionId: 0, subjectId: 0 });

export default {
    name: "mockSubject",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            mockitems: null,
            optionitems: null,
            mocksubjectitems: null,
            availableitems: null,
            selectedMockId: 0,
            selectedOptionId: 0,
            searchval: "",
            mocksFailed: false,
            optionsFailed: false,
            subjectsFailed: false,
            availableFailed: false,
            optionsLoading: false,
            subjectsLoading: false,
            optionSeq: 0,
            subjectSeq: 0,
            formVisible: false,
            isSaving: false,
            form: EMPTY_FORM(),
            formOptions: [],
            formOptionsLoading: false,
            formOptionsFailed: false,
            formOptionSeq: 0,
            formSubjects: [],
            formSubjectsLoading: false,
            formSubjectsFailed: false,
            formSubjectSeq: 0,
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        mocks() {
            return Array.isArray(this.mockitems) ? this.mockitems : [];
        },
        options() {
            return Array.isArray(this.optionitems) ? this.optionitems : [];
        },
        subjects() {
            return Array.isArray(this.mocksubjectitems) ? this.mocksubjectitems : [];
        },
        mocksLoading() {
            return this.mockitems === null;
        },
        selectedMockName() {
            const selected = this.mocks.find(
                item => String(item.Id) === String(this.selectedMockId)
            );
            if (!selected) return "";
            return selected.ExamTitle || selected.Sch_Class || "";
        },
        selectedOptionName() {
            const selected = this.options.find(
                item => String(item.Id) === String(this.selectedOptionId)
            );
            return selected ? selected.ClassOption : "";
        },
        formMockName() {
            const selected = this.mocks.find(
                item => String(item.Id) === String(this.form.mockId)
            );
            if (!selected) return "";
            return selected.ExamTitle || selected.Sch_Class || "";
        },
        formOptionName() {
            const selected = this.formOptions.find(
                item => String(item.Id) === String(this.form.optionId)
            );
            return selected ? selected.ClassOption : "";
        },
        formMatchesScope() {
            return (
                Boolean(this.form.optionId) &&
                String(this.form.mockId) === String(this.selectedMockId) &&
                String(this.form.optionId) === String(this.selectedOptionId)
            );
        },
        availableForForm() {
            if (this.formMatchesScope) {
                return Array.isArray(this.availableitems) ? this.availableitems : [];
            }
            return this.formSubjects;
        },
        availableBusyForForm() {
            return this.formMatchesScope ? this.subjectsLoading : this.formSubjectsLoading;
        },
        availableFailedForForm() {
            return this.formMatchesScope ? this.availableFailed : this.formSubjectsFailed;
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredSubjects() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.subjects;

            return this.subjects.filter(item => {
                const haystack = [item && item.SubjectName]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        scopeLabel() {
            if (this.mocksLoading) return "Loading mock exams…";
            if (this.mocksFailed) return "Mock exams unavailable.";
            if (!this.mocks.length) return "No mock exams available.";
            if (!this.selectedMockId) return "Choose a mock exam to view its subjects.";
            if (this.optionsLoading) return `${this.selectedMockName} · loading options…`;
            if (this.optionsFailed) return `${this.selectedMockName} · options unavailable`;
            if (!this.options.length) return `${this.selectedMockName} · no options`;
            if (!this.selectedOptionId) return `${this.selectedMockName} · choose an option`;
            if (this.subjectsLoading) {
                return `${this.selectedMockName} · ${this.selectedOptionName} · loading subjects…`;
            }
            if (this.subjectsFailed) {
                return `${this.selectedMockName} · ${this.selectedOptionName} · subjects unavailable`;
            }
            if (this.hasSearch) {
                return `${this.selectedMockName} · ${this.selectedOptionName} · ${this.filteredSubjects.length} of ${this.subjects.length} shown`;
            }
            const count = this.subjects.length;
            return `${this.selectedMockName} · ${this.selectedOptionName} · ${count} ${count === 1 ? "subject" : "subjects"}`;
        },
        drawerSubtitle() {
            const scope = [this.formMockName, this.formOptionName]
                .filter(Boolean)
                .join(" · ");
            return scope || "Choose a mock exam, option and subject to add.";
        },
        showFormOptionsNotice() {
            return (
                Boolean(this.form.mockId) &&
                !this.formOptionsLoading &&
                !this.formOptionsFailed &&
                !this.formOptions.length
            );
        },
        showFormSubjectsNotice() {
            return (
                Boolean(this.form.optionId) &&
                Boolean(this.formOptions.length) &&
                !this.availableBusyForForm &&
                !this.availableFailedForForm &&
                !this.availableForForm.length
            );
        },
        submitDisabled() {
            return (
                !this.formOptions.length ||
                this.availableBusyForForm ||
                !this.availableForForm.length
            );
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `mock-subject-${index}`;
        },
        mockLabel(item) {
            if (!item) return "—";
            const label = [item.ExamTitle, item.Sch_Class].filter(Boolean).join(" — ");
            return label || "Untitled mock exam";
        },
        subjectName(item) {
            if (!item) return "—";
            return item.SubjectName ? String(item.SubjectName) : "—";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadMocks() {
            this.mockitems = null;
            this.mocksFailed = false;

            try {
                const resp = await this.$store.dispatch("getmock");
                this.mockitems = Array.isArray(resp) ? resp : [];

                const exists = this.mockitems.some(
                    item => String(item.Id) === String(this.selectedMockId)
                );
                if (!exists) {
                    this.selectedMockId = this.mockitems.length
                        ? this.mockitems[0].Id
                        : 0;
                }

                if (this.selectedMockId) {
                    await this.loadOptions();
                } else {
                    this.optionitems = null;
                    this.mocksubjectitems = null;
                    this.availableitems = null;
                }
            } catch (error) {
                this.mockitems = [];
                this.mocksFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load mock exams at the moment."),
                    ""
                );
            }
        },
        async loadOptions(options = {}) {
            const silent = Boolean(options && options.silent);
            const mockId = this.selectedMockId;

            if (!mockId) {
                this.optionitems = null;
                this.selectedOptionId = 0;
                this.mocksubjectitems = null;
                this.availableitems = null;
                this.optionsLoading = false;
                return;
            }

            const sequence = ++this.optionSeq;
            this.optionsLoading = true;
            this.optionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoptionbymock", mockId);
                if (sequence !== this.optionSeq) return;
                this.optionitems = Array.isArray(resp) ? resp : [];

                const exists = this.optionitems.some(
                    item => String(item.Id) === String(this.selectedOptionId)
                );
                if (!exists) {
                    this.selectedOptionId = this.optionitems.length
                        ? this.optionitems[0].Id
                        : 0;
                }
            } catch (error) {
                if (sequence !== this.optionSeq) return;
                this.optionsFailed = true;
                this.optionitems = [];
                this.selectedOptionId = 0;
                if (!silent) {
                    this.ShowAlertError(
                        this.errorMessage(error, "Unable to load options for this mock exam."),
                        ""
                    );
                }
            } finally {
                if (sequence === this.optionSeq) this.optionsLoading = false;
            }

            if (sequence !== this.optionSeq) return;
            if (this.selectedOptionId) {
                await this.loadSubjects();
            } else {
                this.mocksubjectitems = null;
                this.availableitems = null;
            }
        },
        async loadSubjects() {
            const mockId = this.selectedMockId;
            const optionId = this.selectedOptionId;

            if (!mockId || !optionId) {
                this.mocksubjectitems = null;
                this.availableitems = null;
                this.subjectsLoading = false;
                return;
            }

            const sequence = ++this.subjectSeq;
            this.subjectsLoading = true;
            this.subjectsFailed = false;

            const payload = {
                SubjectId: 1,
                MockId: mockId,
                OptionId: optionId,
                processop: "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            try {
                const [assignedResult, availableResult] = await Promise.all([
                    this.$store
                        .dispatch("getmocksubject", payload)
                        .then(data => ({ data }), error => ({ error })),
                    this.$store
                        .dispatch("getavailablemocksubject", payload)
                        .then(data => ({ data }), error => ({ error }))
                ]);
                if (sequence !== this.subjectSeq) return;

                if (availableResult.error) {
                    this.availableitems = [];
                    this.availableFailed = true;
                } else {
                    this.availableitems = Array.isArray(availableResult.data)
                        ? availableResult.data
                        : [];
                    this.availableFailed = false;
                }

                if (assignedResult.error) {
                    this.mocksubjectitems = [];
                    this.subjectsFailed = true;
                    this.ShowAlertError(
                        this.errorMessage(
                            assignedResult.error,
                            "Unable to load subjects for this mock exam."
                        ),
                        ""
                    );
                } else {
                    this.mocksubjectitems = Array.isArray(assignedResult.data)
                        ? assignedResult.data
                        : [];
                }
            } finally {
                if (sequence === this.subjectSeq) this.subjectsLoading = false;
            }
        },
        retryMocks() {
            this.loadMocks();
        },
        retryOptions() {
            this.optionsFailed = false;
            this.optionitems = null;
            this.selectedOptionId = 0;
            this.mocksubjectitems = null;
            this.availableitems = null;
            this.loadOptions();
        },
        retrySubjects() {
            this.subjectsFailed = false;
            this.mocksubjectitems = null;
            this.loadSubjects();
        },
        onMockChange() {
            this.searchval = "";
            this.selectedOptionId = 0;
            this.optionitems = null;
            this.mocksubjectitems = null;
            this.availableitems = null;
            this.optionsFailed = false;
            this.subjectsFailed = false;
            this.loadOptions();
        },
        onOptionChange() {
            this.searchval = "";
            this.mocksubjectitems = null;
            this.subjectsFailed = false;
            this.loadSubjects();
        },
        async loadFormOptions(preferredOptionId) {
            const mockId = this.form.mockId;
            const sequence = ++this.formOptionSeq;

            if (!mockId) {
                this.formOptions = [];
                this.form.optionId = 0;
                this.formOptionsLoading = false;
                return;
            }

            this.formOptionsLoading = true;
            this.formOptionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoptionbymock", mockId);
                if (sequence !== this.formOptionSeq) return;
                this.formOptions = Array.isArray(resp) ? resp : [];

                const preferred =
                    preferredOptionId === null || preferredOptionId === undefined
                        ? this.form.optionId
                        : preferredOptionId;
                const exists = this.formOptions.some(
                    item => String(item.Id) === String(preferred)
                );
                this.form.optionId = exists
                    ? preferred
                    : this.formOptions.length
                    ? this.formOptions[0].Id
                    : 0;
            } catch (error) {
                if (sequence !== this.formOptionSeq) return;
                this.formOptions = [];
                this.formOptionsFailed = true;
                this.form.optionId = 0;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load options for this mock exam."),
                    ""
                );
            } finally {
                if (sequence === this.formOptionSeq) this.formOptionsLoading = false;
            }

            if (sequence !== this.formOptionSeq) return;
            this.loadFormSubjects();
        },
        async loadFormSubjects() {
            const mockId = this.form.mockId;
            const optionId = this.form.optionId;
            const sequence = ++this.formSubjectSeq;

            this.formSubjectsFailed = false;
            this.form.subjectId = 0;

            if (!mockId || !optionId) {
                this.formSubjects = [];
                this.formSubjectsLoading = false;
                return;
            }

            if (this.formMatchesScope && this.availableitems !== null) {
                this.formSubjects = [];
                this.formSubjectsLoading = false;
                return;
            }

            this.formSubjectsLoading = true;

            try {
                const payload = {
                    SubjectId: 1,
                    MockId: mockId,
                    OptionId: optionId,
                    processop: "N",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                };
                const resp = await this.$store.dispatch(
                    "getavailablemocksubject",
                    payload
                );
                if (sequence !== this.formSubjectSeq) return;
                this.formSubjects = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.formSubjectSeq) return;
                this.formSubjects = [];
                this.formSubjectsFailed = true;
                this.ShowAlertError(
                    this.errorMessage(
                        error,
                        "Unable to load available subjects for this scope."
                    ),
                    ""
                );
            } finally {
                if (sequence === this.formSubjectSeq) this.formSubjectsLoading = false;
            }
        },
        onFormMockChange() {
            this.formOptions = [];
            this.form.optionId = 0;
            this.form.subjectId = 0;
            this.formSubjects = [];
            this.formSubjectsFailed = false;
            this.formSubjectsLoading = false;
            this.formSubjectSeq += 1;
            this.clearError("mockId");
            this.clearError("optionId");
            this.clearError("subjectId");
            this.loadFormOptions(0);
        },
        onFormOptionChange() {
            this.form.subjectId = 0;
            this.formSubjects = [];
            this.clearError("optionId");
            this.clearError("subjectId");
            this.loadFormSubjects();
        },
        resetForm() {
            this.form = EMPTY_FORM();
            this.errors = {};
            this.formOptions = [];
            this.formOptionsLoading = false;
            this.formOptionsFailed = false;
            this.formSubjects = [];
            this.formSubjectsLoading = false;
            this.formSubjectsFailed = false;
            this.formOptionSeq += 1;
            this.formSubjectSeq += 1;
        },
        openAssign() {
            if (!this.mocks.length) return;
            this.resetForm();
            this.form = {
                mockId: this.selectedMockId || 0,
                optionId: this.selectedOptionId || 0,
                subjectId: 0
            };
            this.formVisible = true;
            if (this.form.mockId) {
                this.loadFormOptions(this.form.optionId);
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};

            const mockId = this.form.mockId;
            if (
                mockId === 0 ||
                mockId === "0" ||
                mockId === "" ||
                mockId === null ||
                mockId === undefined
            ) {
                errors.mockId = "Choose the mock exam for this subject.";
            }

            const optionId = this.form.optionId;
            if (
                optionId === 0 ||
                optionId === "0" ||
                optionId === "" ||
                optionId === null ||
                optionId === undefined
            ) {
                errors.optionId = "Choose the option this subject belongs to.";
            }

            const subjectId = this.form.subjectId;
            if (
                subjectId === 0 ||
                subjectId === "0" ||
                subjectId === "" ||
                subjectId === null ||
                subjectId === undefined
            ) {
                errors.subjectId = "Choose the subject to add.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    mockId: "mock-class",
                    optionId: "mock-option",
                    subjectId: "mock-subject"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async assignSubject() {
            if (this.isSaving) return;
            if (!this.formOptions.length) return;
            if (this.availableBusyForForm) return;
            if (!this.availableForForm.length) return;
            if (!this.validate()) return;

            const savedMock = this.form.mockId;
            const savedOption = this.form.optionId;
            const payload = {
                SubjectId: this.form.subjectId,
                MockId: savedMock,
                OptionId: savedOption,
                processop: "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("registermocksubject", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The subject could not be added.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                this.selectedMockId = savedMock;
                this.selectedOptionId = savedOption;
                this.optionitems = null;
                await this.loadOptions({ silent: true });

                if (this.optionsFailed || this.subjectsFailed) {
                    this.ShowAlertWarning(
                        "Subject added, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess("Subject added to the mock exam successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to add this subject at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        },
        async removeSubject(item) {
            if (!item || this.isSaving) return;

            const name = this.subjectName(item);
            const confirmed = await this.showSchooldockAlert({
                title: "Remove subject?",
                msg: `${name} will be removed from ${this.selectedMockName} · ${this.selectedOptionName}.`,
                icon: "warning",
                confirmButtonColor: "#c23b4a",
                showCancelButton: true
            });
            if (!confirmed || !confirmed.isConfirmed) return;

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const record = await this.$store.dispatch("getmocksubjectbyid", item.Id);
                if (!record) throw new Error("The selected mock subject could not be loaded.");

                const payload = {
                    SubjectId:
                        record.SubjectId !== undefined && record.SubjectId !== null
                            ? record.SubjectId
                            : item.Id,
                    MockId: record.MockId,
                    OptionId: record.OptionId,
                    processop: "D",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                };
                const resp = await this.$store.dispatch("registermocksubject", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The subject could not be removed.",
                        ""
                    );
                    return;
                }

                await this.loadSubjects();

                if (this.subjectsFailed) {
                    this.ShowAlertWarning(
                        "Subject removed, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess("Subject removed from the mock exam successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to remove this subject at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadMocks();
    }
};
</script>
